import React from 'react';

interface KnownPlacement {
  id?: string;
  _id?: { $oid: string };
  charm_id: string;
  charm_name: string;
  posx: number;
  posy: number;
  x: number;
  y: number;
  z: number;
  islegacy: boolean;
  isactive: boolean;
  inspectlink?: string;
  charmlink_image?: string; // Added for loading the actual picture
}

interface Charmlink {
  _id: { $oid: string };
  def_index: number;
  paint_index: number;
  asset_id: { $numberLong: string };
  offset_x: number;
  offset_y: number;
  offset_z: number;
  slot: number;
  sticker_id: number;
  keychain: any;
  S: { $numberLong: string };
  A: { $numberLong: string };
  D: string;
  M: any;
  inspectlink: string;
  isactive?: boolean;
  isskip?: boolean;
  isloaded?: boolean;
}

interface SidebarProps {
  weaponDefindexMap: Record<string, number>;
  selectedWeapon: string;
  onWeaponSelect: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  knownPlacements: KnownPlacement[];
  onPlacementClick: (placement: KnownPlacement) => void;
  currentPlacementId?: string;
  newPlacements: Charmlink[];
  imageReadyCount: number;
  totalAllCount: number;
  onNewPlacementLoad: (placement: Charmlink) => void;
  onNewPlacementSkip: (placement: Charmlink) => void;
  weaponCounts?: Record<string, { existing: { new: number; legacy: number }; available: { new: number; legacy: number } }>;
  fetchingCharmlinks: boolean;
  currentUser?: string;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  weaponDefindexMap,
  selectedWeapon,
  onWeaponSelect,
  knownPlacements,
  onPlacementClick,
  currentPlacementId,
  newPlacements,
  imageReadyCount,
  totalAllCount,
  onNewPlacementLoad,
  onNewPlacementSkip,
  weaponCounts,
  fetchingCharmlinks,
  currentUser,
  onLogout
}) => {
  return (
    <div className="sidebar">
      {/* Weapon Selection */}
      <div className="sidebar-section">
        <h3>Weapon Selection</h3>
        <select
          value={selectedWeapon}
          onChange={onWeaponSelect}
          className="weapon-select"
          disabled={fetchingCharmlinks}
        >
          <option value="">{fetchingCharmlinks ? 'Loading...' : 'Select a weapon...'}</option>
          {Object.entries(weaponDefindexMap).map(([name, item_id]) => {
            const existingNew = weaponCounts?.[name]?.existing?.new || 0;
            const existingLegacy = weaponCounts?.[name]?.existing?.legacy || 0;
            const availableNew = weaponCounts?.[name]?.available?.new || 0;
            const availableLegacy = weaponCounts?.[name]?.available?.legacy || 0;
            return [
              <option key={`${name}-new`} value={`${name}-new`}>
                {name} (new) {existingNew} / {availableNew}
              </option>,
              <option key={`${name}-legacy`} value={`${name}-legacy`}>
                {name} (legacy) {existingLegacy} / {availableLegacy}
              </option>
            ];
          })}
        </select>
      </div>

      {/* Existing Placements */}
      <div className="sidebar-section">
        <h3>Existing Placements</h3>
        <div className="placements-list">
          {knownPlacements.length > 0 ? (
            knownPlacements.filter(p => p.isactive).map((placement, index) => {
              const idValue = typeof placement._id === 'object' && placement._id?.$oid
                ? placement._id.$oid
                : typeof placement._id === 'string'
                  ? placement._id
                  : placement.id || 'No ID';
              
              return (
                <div
                  key={idValue || index}
                  className={`placement-item ${currentPlacementId === idValue ? 'active' : ''}`}
                  onClick={() => onPlacementClick(placement)}
                >
                  <div className="placement-id">{idValue}</div>
                  <div className="placement-name">{placement.charm_name}</div>
                </div>
              );
            })
          ) : (
            <div className="no-placements">
              No known placements for this weapon
            </div>
          )}
        </div>
      </div>



             {/* New Placements */}
       <div className="sidebar-section">

         <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>New Placements</h3>
         {imageReadyCount > 0 && (
           <div style={{ 
             fontSize: '12px', 
             color: 'var(--text-muted)',
             backgroundColor: 'var(--bg-tertiary)',
             padding: '2px 6px',
             borderRadius: '10px',
             fontWeight: '500',
             display: 'inline-block',
             marginBottom: '8px'
           }}>
             {imageReadyCount} / {totalAllCount} image ready
           </div>
         )}
         {fetchingCharmlinks && (
           <div style={{ 
             fontSize: '12px', 
             color: 'var(--accent-primary)',
             backgroundColor: 'rgba(102, 192, 244, 0.2)',
             padding: '4px 8px',
             borderRadius: '4px',
             fontWeight: '500',
             display: 'flex',
             alignItems: 'center',
             gap: '6px',
             marginBottom: '8px'
           }}>
             Fetching charmlinks...
           </div>
         )}
                                   <div className="new-placements">
            {newPlacements.length > 0 ? (
              <div className="placement-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{ flex: 1, cursor: 'pointer', color: newPlacements[0].isloaded ? 'var(--text-muted)' : 'var(--text-primary)' }}
                  title={newPlacements[0].inspectlink}
                  onClick={() => !newPlacements[0].isloaded && onNewPlacementLoad(newPlacements[0])}
                >
                  Next
                </div>
              </div>
            ) : (
              <div className="placeholder-text">
                Next
              </div>
            )}             {/* User Info and Logout - positioned higher for small screens */}
             <div className="sidebar-section" style={{ paddingTop: '4px', marginTop: '8px', borderTop: '1px solid var(--border-primary)' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '6px',
          alignItems: 'stretch'
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: 'var(--success)', 
            fontWeight: '500',
            padding: '4px 8px',
            backgroundColor: 'rgba(76, 175, 80, 0.2)',
            border: '1px solid var(--success)',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {currentUser}
          </div>
          <button
            onClick={onLogout}
            style={{
              fontSize: '12px',
              padding: '6px 12px',
              backgroundColor: 'var(--error)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              width: '100%'
            }}
            title="Logout"
          >
            Logout
          </button>
        </div>
      </div>
          </div>
      </div>

    </div>
  );
};

export default Sidebar; 